import { enableProdMode, LOCALE_ID, importProvidersFrom, inject, provideEnvironmentInitializer } from '@angular/core';
import { environment } from 'environments/environment';
import { vimCoreSentry, provideVimCoreSentryConfig } from '@skyeng/libs/vimbox-core/sentry';
import { VimCoreBlocksStoreApi } from '@skyeng/libs/vimbox-core/lesson/blocks';
import { SafeBlockApiService } from './app/shared/service/safe-block-api';
import {
  provideTranslocoConfig,
  registerAllLocalesData,
  registerTranslationsManuallyStatic,
} from '@skyeng/libs/shared/i18n';
import skyproRoutingPageRu from './app/shared/routing/translation/ru.json';
import skyproRoutingPageEn from './app/shared/routing/translation/en.json';
import { skyproAvailableLanguages, SkyproAvailableLanguages } from '@skyeng/libs/skypro/localization';
import {
  IVimCoreTrackingUserData,
  provideVimCoreTrackingConfig,
  VimCoreTrackingService,
} from '@skyeng/libs/vimbox-core/tracking';
import { getBaseConfig } from '@skyeng/libs/skypro/base-config/utils';
import { ISkyCryptoConfig, SkyCryptoModule } from '@skyeng/libs/shared/crypto';
import { ISkyResourceConfig, SkyResourceModule } from '@skyeng/libs/shared/resource';
import {
  SkyproTrackingProvider,
  CustomSentryErrorHandler,
  VersionCheckerModule,
  GtmService,
} from '@skyeng/libs/skypro/misc';
import { provideVimCoreBaseConfig } from '@skyeng/libs/vimbox-core/base';
import { provideVimCoreUserConfig, VimCoreCurrentUserService, VimCoreAuthService } from '@skyeng/libs/vimbox-core/user';
import { provideSkyproCommunicationConfig } from '@skyeng/libs/skypro/communications';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './app/shared/routing/routing/routes';
import { SkyApiModule } from '@skyeng/libs/shared/api';
import { SkyUiMainColorsService, SkyUiPopupModule, SkyUiTooltipModule } from '@skyeng/libs/shared/ui-kit';
import { ReactiveFormsModule } from '@angular/forms';
import { NgxMaskModule } from 'ngx-mask';
import { QuillModule } from 'ngx-quill';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { TranslocoMessageFormatModule } from '@ngneat/transloco-messageformat';
import { SkyproBaseConfigModule } from '@skyeng/libs/skypro/base-config/module';
import { AppComponent } from './app/app.component';
import { PYTHON_WORKER_FACTORY } from '@skyeng/libs/skypro/blocks/python-exercise';
import { createSkyproPythonWorker } from './workers/skypro-python-webworker.factory';
import { bootstrapApplication } from '@angular/platform-browser';
import { SkyDeviceService, USER_AGENT } from '@skyeng/libs/shared/device';
import { RoutingHelpersService } from '@skyeng/libs/vimbox-core/routing';
import {
  ERROR_REPORT_APP_VERSION,
  ERROR_REPORT_LEGACY_CONFIG,
  ERROR_REPORT_PRODUCT_KEY,
  ERROR_REPORT_PROXY_ENDPOINT,
  ERROR_REPORT_UPLOAD_PROVIDER,
  ErrorReportApiService,
  ProxyErrorReportUploadService,
} from '@skyeng/libs/laboratory/error-report-api';
import { REPORT_CHAT_SOCKET_BASE_URL } from '@skyeng/libs/laboratory/report-chat';
import { SkyDialogWithAttachmentsAndOptionsApiProvider } from '@skyeng/libs/shared/popups';

registerAllLocalesData();

const baseConfig = getBaseConfig(environment.app.rootDomain);
const routeLoginPageResolver = (): string => {
  return `${baseConfig.loginUrl}&redirect=${encodeURIComponent(window.location.href)}`;
};
function skyCryptoConfigFactory(): ISkyCryptoConfig {
  return environment.crypto;
}
function skyResourceConfigFactory(): ISkyResourceConfig {
  return {
    contentEndpoints: [`https://content-vimbox.${environment.app.rootDomain}/`],
  };
}

if (environment.app.production) {
  enableProdMode();
}

vimCoreSentry.init({
  dsn: environment.sentry.dsn,
  appName: environment.app.name,
  release: environment.app.buildNumber.toString(),
  enableHealthCheck: true,
});

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: PYTHON_WORKER_FACTORY,
      useValue: (): Worker => createSkyproPythonWorker(),
    },
    provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })),
    importProvidersFrom(
      SkyApiModule.forRoot(),
      SkyCryptoModule.forRoot(skyCryptoConfigFactory),
      // required to import here for proper SkyUiPopupService injection scope
      SkyUiPopupModule,
      ReactiveFormsModule,
      SkyUiTooltipModule,
      SkyResourceModule.forRoot(skyResourceConfigFactory),
      NgxMaskModule.forRoot(),
      QuillModule.forRoot(),
      TranslocoModule,
      TranslocoMessageFormatModule.forRoot(),
      SkyproBaseConfigModule.forRoot(environment.app.rootDomain),
      VersionCheckerModule,
    ),
    { provide: LOCALE_ID, useValue: 'ru' },
    { provide: VimCoreBlocksStoreApi, useClass: SafeBlockApiService },
    { provide: ERROR_REPORT_PRODUCT_KEY, useValue: 'student-cabinet' as const },
    { provide: ERROR_REPORT_PROXY_ENDPOINT, useValue: [environment.endpoints.speechRecognitionProxy] },
    { provide: ERROR_REPORT_LEGACY_CONFIG, useValue: null },
    { provide: ERROR_REPORT_UPLOAD_PROVIDER, useExisting: ProxyErrorReportUploadService },
    { provide: ERROR_REPORT_APP_VERSION, useValue: String(environment.app.buildNumber) },
    { provide: REPORT_CHAT_SOCKET_BASE_URL, useValue: environment.endpoints.speechRecognitionProxy },
    ErrorReportApiService,
    { provide: SkyDialogWithAttachmentsAndOptionsApiProvider, useExisting: ErrorReportApiService },
    provideEnvironmentInitializer(() => {
      const vimCoreTrackingService = inject(VimCoreTrackingService);
      const routingHelpersService = inject(RoutingHelpersService);
      const skyUiMainColorsService = inject(SkyUiMainColorsService);
      const translocoService = inject(TranslocoService);
      const vimCoreCurrentUserService = inject(VimCoreCurrentUserService);
      const gtmService = inject(GtmService);
      const vimCoreAuthService = inject(VimCoreAuthService);

      // Пытаемся авторизовать юзера, чтобы внутри страниц была возможность получить userID из курент юзер сервиса
      vimCoreAuthService.authorize().subscribe();

      registerTranslationsManuallyStatic('skyproRoutingPage', translocoService, {
        ru: skyproRoutingPageRu,
        en: skyproRoutingPageEn,
      });

      if (environment.app.production) {
        gtmService.init();
      }

      skyUiMainColorsService.setMainColor('#5d9cec');

      translocoService.setDefaultLang('ru');
      translocoService.setActiveLang('ru');

      vimCoreCurrentUserService.authorizedUser$.subscribe(user => {
        const userData: IVimCoreTrackingUserData = {
          name: user.name,
          user_role: user.roles.join(),
          role: user.roles.join(),
          app_name: environment.app.name,
        };

        gtmService.pushData({ user_id: user.id });
        vimCoreTrackingService.setUserData(user.id, userData);
        trackPageChanges(routingHelpersService, vimCoreTrackingService);
      });
    }),
    provideTranslocoConfig({
      availableLangs: skyproAvailableLanguages,
      defaultLang: SkyproAvailableLanguages.en,
      fallbackLang: SkyproAvailableLanguages.en,
    }),
    provideVimCoreTrackingConfig({
      enabled: environment.tracking.enabled,
      debug: !environment.tracking.skyengKey,
      source: environment.app.name,
      providers: [
        {
          useClass: SkyproTrackingProvider,
          key: environment.tracking.skyengKey,
        },
      ],
    }),
    provideVimCoreBaseConfig({
      rootDomain: environment.app.rootDomain,
      boilerplateEndpoint: baseConfig.apiUrl,
      blocksStoreEndpoint: environment.endpoints.storeBlocks,
    }),
    provideVimCoreSentryConfig({ customErrorHandler: CustomSentryErrorHandler }),
    provideVimCoreUserConfig({
      authGuard: {
        routeLoginPageResolver,
      },
      authJwtHeaderInterceptor: {
        enable: true,
        includeEndpoints: [environment.endpoints.storeBlocks],
      },
    }),
    provideSkyproCommunicationConfig({ communicationApiDomain: environment.endpoints.api }),
    provideAnimations(),
  ],
}).catch(error => vimCoreSentry.captureException(error));

function trackPageChanges(
  routingHelpersService: RoutingHelpersService,
  vimCoreTrackingService: VimCoreTrackingService,
): void {
  routingHelpersService.routeChanged$.subscribe(() => {
    const routeData = routingHelpersService.getCurrentRouteData<{ pageId?: string }>();
    const pageId = routeData.pageId;

    if (!pageId) {
      return;
    }

    const context = {
      mobile: SkyDeviceService.isMobile(),
      device_type: USER_AGENT.device.type ? USER_AGENT.device.type : 'desktop',
      environment: SkyDeviceService.getEnvironment(),
      os_name: USER_AGENT.os.name,
      os_version: USER_AGENT.os.version,
      browser_name: USER_AGENT.browser.name,
      browser_version: USER_AGENT.browser.version,
    };

    vimCoreTrackingService.page(pageId, context);
  });
}
